import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import skyRPLogo from "../assets/images/sky-rp-logo.png";
import { durationHHMM } from "../utils/common";
import ReportSummaryChart from "./ReportSummaryChart";
import {
  exportReportPdf,
  exportReportSheet,
  exportSectionsPdf,
  exportSectionsSheet,
} from "../utils/reportExport";
import "../assets/css/reportViewer.css";

/* Sheet sizes in millimetres. A wide report needs landscape or the columns
   are squeezed to unreadable slivers — with two dozen columns A4 portrait
   gives each one about 7mm. */
const PAPERS = {
  a4p: { label: "A4 Portrait", w: 210, h: 297 },
  a4l: { label: "A4 Landscape", w: 297, h: 210 },
  a3l: { label: "A3 Landscape", w: 420, h: 297 },
};

const ZOOM_STEPS = [40, 50, 75, 100, 125, 150, 200];

/* Pagination budget, in text lines. A row is not one line: a long Nature of
   Query wrapped into a narrow column can be five, and counting rows instead
   of lines is what makes a "page" spill halfway down the next sheet.
   ~7.2mm of vertical space per text line at this font size. */
const MM_PER_LINE = 7.2;
const CHROME_MM = 24 + 10; // page padding + footer
const HEADER_MM = 30; // letterhead, first page only

/* Roughly how wide a character is, used to guess how many lines a cell wraps
   to. The dense font (past 15 columns) is noticeably narrower. */
const PX_PER_CHAR = 5;
const PX_PER_CHAR_DENSE = 4.1;

/* The shortest the embedded report may shrink to. Matches the `min-height` on
   .rv-overlay--embedded — a smaller number here would be silently overridden
   by the stylesheet and the page would keep the scrollbar anyway. */
const MIN_FIT = 420;

/* A hair of breathing room under the report so it does not sit flush against
   the window edge. Small on purpose — every pixel here is table the reader
   does not get. */
const BOTTOM_GAP = 6;

const MM_TO_PX = 96 / 25.4;
const PAGE_PADDING_MM = 20; // 10mm each side, matches .rv-page

/* The formats the Export menu offers. XLS is the 2000-2003 workbook, kept
   because plenty of desk software still only opens that. */
const EXPORT_FORMATS = [
  {
    key: "pdf",
    tag: "PDF",
    tone: "red",
    name: "PDF File",
    note: "Adobe Portable Document Format",
  },
  {
    key: "xlsx",
    tag: "XLSX",
    tone: "green",
    name: "Excel File",
    note: "Microsoft Excel Workbook",
  },
  {
    key: "biff8",
    tag: "XLS",
    tone: "green",
    name: "Excel File",
    note: "Microsoft Excel 2000-2003 Workbook",
  },
];

const asNumber = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  const n = Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};

/* A column marked kind:"duration" carries MINUTES so it can be summed, and is
   shown as HH:MM. Keeping the raw number in the data and formatting only at
   the edge is what lets the sub-totals add up while the report still reads in
   hours — a column of "113" and "2315" tells nobody anything. */
/* null means "this row has nothing to say here", and it is not the same as
   zero. A duration column that renders null as "00:00" claims a measurement
   nobody made — which is how a task split across several sittings came to
   show a full estimate against each sitting. Blank is the honest cell;
   asNumber above already reads null as 0 for the column total. */
const displayCell = (col, raw) =>
  col.kind === "duration"
    ? raw === null || raw === undefined
      ? ""
      : durationHHMM(raw)
    : raw;

const displayTotal = (col, sum) =>
  col.kind === "duration" ? durationHHMM(sum) : fmtNumber(sum);

const fmtNumber = (n) =>
  Number.isInteger(n) ? n.toLocaleString("en-IN") : n.toFixed(2);

const fmtDate = (d) => {
  if (!d) return "";
  const date = d instanceof Date ? d : new Date(d);
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
};

/**
 * A printable report document.
 *
 * Takes the same rows a grid shows and lays them out as a paginated A4
 * document: letterhead, optional grouping with sub-totals, column totals,
 * page numbers, and a print that matches the screen.
 *
 * Props:
 *   show, onClose
 *   title      : "Detailed Query Report"
 *   company?   : letterhead name (defaults to SkyEye)
 *   dateRange? : { startDate, endDate }
 *   columns    : [{ key, label, width?, numeric?, total?, value?, cellStyle? }]
 *                value(row) overrides row[key]; total marks a column to sum;
 *                cellStyle(value, row) returns a style for that one cell, for
 *                colouring a column by what it holds. Returning nothing
 *                leaves the cell plain, so a column can tint its exceptions
 *                and stay quiet everywhere else.
 *   rows       : the data
 *   groupBy?   : { key, label, value?(row) } — omit for a flat list
 *   onExport?  : (kind) => void   — 'pdf' | 'excel', shown in the Export menu
 *   embedded?  : render in the page instead of as a full-screen overlay,
 *                for when the report IS the page rather than a preview of it
 */
const ReportViewer = ({
  show,
  onClose,
  title,
  company = "SKYEYE — TASK MANAGEMENT",
  subtitle,
  dateRange,
  columns: allColumns = [],
  rows = [],
  groupBy = null,
  onExport,
  embedded = false,
  /* Landscape by default: these reports carry more columns than an A4
     portrait sheet can hold at a readable size. */
  defaultPaper = "a4l",
  /* Two bars instead of one.
   *
   * The single strip had to hold everything: what the report IS (which dates,
   * which grouping, where the Export is) alongside how you are LOOKING at it
   * (page 3 of 28, 100%, sixteen columns). Those are different questions, and
   * mixing them meant the reader hunted the same row for both.
   *
   * Split, the top bar carries the report's identity and the two things you
   * do to the whole of it — export it, leave it — and the dark bar underneath
   * carries the viewing controls. Opt-in, because Over All and Punching are
   * still one-bar screens and a chrome that changed under them would be a
   * change nobody asked for. */
  splitChrome = false,
  /* Chips the page puts at the left of the context bar — the date window, the
     report type, its filters. The viewer does not know what they mean; it
     knows where they go. */
  contextLeft = null,
  backLabel = "Back",
  /* Mounted but off screen is a real state now: the page that owns the setup
     screen hides the report rather than unmounting it, so the zoom, the page
     you were on and the columns you switched off survive a trip back.
     Hidden, the element measures zero — so the height fit has to know. */
  visible = true,
  /* A document made of several tables rather than one.
   *
   * Everything else here assumes one column set: the widths, the page-break
   * estimate, the summary, the column picker. A management report is three
   * tables with nothing in common but the sheet they are printed on, so it
   * gets its own path — `sections` is [{ key, title, columns, rows }] and,
   * when present, replaces the single table entirely.
   *
   * Kept as a separate branch rather than folded into the main one: the
   * reports that have worked for months are not worth the risk of teaching
   * the single-table path to be two things at once. */
  sections = null,
  /* A summary table printed at the END of the detail report, rather than
     instead of it.
     *
     * The Summary toggle swaps one view for the other, which is right on
     * screen and wrong on paper: a printed report is read away from the
     * screen by somebody who cannot press the toggle, and the totals are what
     * they turn to the back for. */
  appendSummary = false,
  /* The dimension that summary counts by when the report itself is not
     grouped. Without it a detailed report has nothing to summarise BY — one
     row of grand totals, which the last line of the detail already says. */
  summaryBy = null,
  /* Extra count columns on the summary tables, one per segment.
   *
   * A summary says how many tasks each person had; it could not say how many
   * of them were Common Task work and how many were the tech team's, which is
   * the split the two queues are actually raised under. Passed in rather than
   * built here because the viewer has no business knowing what IsSupportTeam
   * means — it counts rows against a test it is handed.
   *
   * [{ key, label, test(row) }] */
  summarySegments = null,
}) => {
  /* Detail or summary. Held here rather than by the page because it is a way
     of looking at the same report, not a different report. */
  const [summary, setSummary] = useState(false);
  const [zoom, setZoom] = useState(100);
  const [paperKey, setPaperKey] = useState(defaultPaper);
  const [fitHeight, setFitHeight] = useState(null);
  const [page, setPage] = useState(0);
  const [showExport, setShowExport] = useState(false);
  const [exporting, setExporting] = useState(false);
  /* Keys switched off. Everything is on to begin with — the caller decides
     what the report contains; this only lets the reader thin it out when two
     dozen columns turn out to be more than they wanted on paper. */
  const [hidden, setHidden] = useState([]);
  const [showColumns, setShowColumns] = useState(false);
  /* An embedded report lives deep in the app tree, where the print
     stylesheet's "hide everything that is not the report" rule would hide its
     own ancestors along with the chrome. Rather than a second, more fragile
     set of print rules, it hops into the body-level portal for the duration
     of the print and back afterwards. */
  const [printMode, setPrintMode] = useState(false);
  const scrollRef = useRef(null);
  const pageRefs = useRef([]);
  const overlayRef = useRef(null);

  const usePortal = !embedded || printMode;
  const paper = PAPERS[paperKey] ?? PAPERS.a4l;

  /* Fill exactly what is left of the window.

     Two numbers decide that: where the report starts, and how much of the
     window is spoken for BELOW it. The first is measured from the element.
     The second is the padding and margin of every ancestor between the report
     and the body — the page shell's own bottom padding, mostly.

     Both are measured, neither is guessed. An earlier version set the height
     from the top edge alone and then trimmed by however much the page
     overflowed; that removed the scrollbar but left a band of dead grey under
     the report, because a trim can only ever take space away and had no way
     to know it had taken too much. Reading the reserved space directly gives
     the right answer in one pass, with nothing to settle and nothing to
     oscillate. */
  useEffect(() => {
    /* `visible` matters as much as `embedded` here. A display:none element
       reports a top of 0, which would size the report to the whole window and
       leave it overflowing the page the moment it was shown. */
    if (!embedded || printMode || !visible) return;

    const measure = () => {
      const el = overlayRef.current;
      if (!el) return;

      const top = el.getBoundingClientRect().top;

      /* Walk up to the body adding what each ancestor keeps for itself under
         us. Padding and margin only — their heights are what we are deciding,
         so reading those would be circular. */
      let below = 0;
      for (let n = el.parentElement; n && n !== document.body; n = n.parentElement) {
        const cs = window.getComputedStyle(n);
        below += parseFloat(cs.paddingBottom) || 0;
        below += parseFloat(cs.marginBottom) || 0;
      }

      setFitHeight(Math.max(MIN_FIT, window.innerHeight - top - below - BOTTOM_GAP));
    };

    measure();
    window.addEventListener("resize", measure);

    /* The page header above can reflow after fonts and chips load, which
       moves our top edge without firing a resize. */
    const ro = new ResizeObserver(measure);
    if (document.body) ro.observe(document.body);

    return () => {
      window.removeEventListener("resize", measure);
      ro.disconnect();
    };
  }, [embedded, printMode, visible]);

  const printedAt = useMemo(
    () =>
      show
        ? new Date().toLocaleString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })
        : "",
    [show],
  );

  const cellValue = useCallback(
    (col, row) => (col.value ? col.value(row) : row[col.key]),
    [],
  );

  /* Only the columns the user has left switched on. */
  const columns = useMemo(
    () => allColumns.filter((c) => !hidden.includes(c.key)),
    [allColumns, hidden],
  );

  const dense = columns.length > 15;

  /* Columns keep the width they ask for, and the SHEET grows to hold them.
     Squeezing two dozen columns into a fixed sheet is what produced the
     mess: every column came out about 40px, so headers collided into
     "ManuSuppo" and "TEXTILE INDUSTRY" broke into four pieces mid-word.

     Scrolling sideways to read a wide report is the normal trade — a
     narrower column that cannot hold its own text is not. The paper picker
     still sets the height and the minimum width; a report that fits keeps
     the standard size, and only a genuinely wide one grows past it. */
  const layout = useMemo(() => {
    const SERIAL_PX = 34;
    const widths = columns.map((c) => parseFloat(c.width) || 60);
    const contentPx = widths.reduce((s, w) => s + w, SERIAL_PX);

    const neededMm = contentPx / MM_TO_PX + PAGE_PADDING_MM;
    const perChar = dense ? PX_PER_CHAR_DENSE : PX_PER_CHAR;

    return {
      pageWidthMm: Math.max(paper.w, Math.ceil(neededMm)),
      serialPx: SERIAL_PX,
      widths,
      /* Straight from the real rendered width now, so the page-break
         estimate matches what the browser will actually lay out. */
      charsPerLine: widths.map((w) => Math.max(3, Math.floor(w / perChar))),
    };
  }, [columns, paper.w, dense]);

  /* Turn the rows into a flat list of printable LINES — a group header, a
     data row and a subtotal are all one line each. Paginating this list
     rather than the raw rows is what keeps a group header attached to the
     rows underneath it. */
  const lines = useMemo(() => {
    const out = [];
    let serial = 0;

    const totalsFor = (bucket) => {
      const t = {};
      columns.forEach((c) => {
        if (c.total) t[c.key] = bucket.reduce((s, r) => s + asNumber(cellValue(c, r)), 0);
      });
      return t;
    };

    if (!groupBy) {
      rows.forEach((r) => out.push({ kind: "row", row: r, serial: ++serial }));
      if (rows.length && columns.some((c) => c.total)) {
        out.push({ kind: "grand", totals: totalsFor(rows), count: rows.length });
      }
      return out;
    }

    const keyOf = (r) =>
      String(
        (groupBy.value ? groupBy.value(r) : r[groupBy.key]) ?? "",
      ).trim() || "(none)";

    const buckets = new Map();
    rows.forEach((r) => {
      const k = keyOf(r);
      if (!buckets.has(k)) buckets.set(k, []);
      buckets.get(k).push(r);
    });

    [...buckets.keys()]
      .sort((a, b) => a.localeCompare(b))
      .forEach((k) => {
        const bucket = buckets.get(k);
        out.push({ kind: "group", label: k, count: bucket.length });
        bucket.forEach((r) => out.push({ kind: "row", row: r, serial: ++serial }));
        out.push({ kind: "subtotal", totals: totalsFor(bucket), count: bucket.length });
      });

    if (rows.length && columns.some((c) => c.total)) {
      out.push({ kind: "grand", totals: totalsFor(rows), count: rows.length });
    }

    return out;
  }, [rows, columns, groupBy, cellValue]);

  /* The summary: one row per group, no detail. Read straight off the lines
     already computed, so the summary and the detail report can never disagree
     about a total — they are the same arithmetic. */
  /* The summary, built once and used by both places that show one — the
     Summary toggle and the sheet appended to the detail report. They used to
     compute it separately, one off `lines` and one off `rows`, which is two
     chances to disagree about the same totals.

     `summarySegments` turns it into SEVERAL summaries rather than extra
     columns on one: a task raised through the Common Team screen and a task
     raised for the tech team are different work, and reading them as two
     tables side by side is how the two queues are actually managed. Without
     segments there is exactly one table and nothing changes. */
  /* The summary, built once and used by both places that show one — the
     Summary toggle and the sheet appended to the detail report.

     ONE table, with the queues as columns.
     *
     * They were briefly two tables side by side, which does not survive the
     * page: a summary is already seven columns wide, and two of them left the
     * second running off the right-hand edge of an A4 sheet. Merged, every
     * person is one line and the reader compares their two queues by looking
     * left and right rather than across two tables with different row orders.
     *
     * `summarySegments` supplies the extra count columns; without it the
     * table is exactly what it always was. */
  const buildSummary = useCallback(
    (subset, dim, segments) => {
      const totalCols = columns.filter((c) => c.total);
      const buckets = new Map();

      subset.forEach((r) => {
        const label =
          String((dim.value ? dim.value(r) : r[dim.key]) ?? "").trim() ||
          "(none)";
        if (!buckets.has(label))
          buckets.set(label, { label, count: 0, totals: {}, segments: {} });
        const b = buckets.get(label);
        b.count += 1;
        totalCols.forEach((c) => {
          b.totals[c.key] = (b.totals[c.key] || 0) + asNumber(cellValue(c, r));
        });
        (segments || []).forEach((seg) => {
          if (seg.test(r)) b.segments[seg.key] = (b.segments[seg.key] || 0) + 1;
        });
      });

      const list = [...buckets.values()].sort((a, b) =>
        a.label.localeCompare(b.label),
      );

      const totals = { count: subset.length, segments: {} };
      totalCols.forEach((c) => {
        totals[c.key] = list.reduce((n, b) => n + (b.totals[c.key] || 0), 0);
      });
      (segments || []).forEach((seg) => {
        totals.segments[seg.key] = list.reduce(
          (n, b) => n + (b.segments[seg.key] || 0),
          0,
        );
      });

      return { rows: list, totals, totalCols, segments: segments || [] };
    },
    [columns, cellValue],
  );

  const summaryDim = groupBy || summaryBy;

  const summaryTable = useMemo(
    () =>
      summaryDim && rows.length
        ? buildSummary(rows, summaryDim, summarySegments)
        : null,
    [rows, summaryDim, summarySegments, buildSummary],
  );

  /* The chart above the summary reads the same rows the table does. */
  const summaryRows = summaryTable?.rows ?? [];

  const tailSummary = useMemo(
    () =>
      appendSummary && summaryTable
        ? { label: summaryDim.label, table: summaryTable }
        : null,
    [appendSummary, summaryDim, summaryTable],
  );


  /* Summary is only meaningful with a grouping — without one it would be a
     single row saying what the grand total already says. */
  const canSummarise = Boolean(groupBy) && summaryRows.length > 0;
  const showSummary = summary && canSummarise;

  /* How many text lines a row will occupy once its cells wrap. Driven by the
     widest-wrapping cell, because that is what sets the row's height. */
  const lineWeight = useCallback(
    (line) => {
      if (line.kind !== "row") return 1;

      let tallest = 1;
      columns.forEach((c, i) => {
        const perLine = layout.charsPerLine[i];
        const text = String(cellValue(c, line.row) ?? "");
        if (text.length > perLine) {
          tallest = Math.max(tallest, Math.ceil(text.length / perLine));
        }
      });

      /* Cap it: one pathological cell should cost a chunk of the page, not
         the whole sheet, or a single long description leaves a page holding
         one row. */
      return Math.min(tallest, 8);
    },
    [columns, cellValue, layout],
  );

  const pages = useMemo(() => {
    if (!lines.length) return [[]];

    const budget = Math.max(
      8,
      Math.floor((paper.h - CHROME_MM) / MM_PER_LINE),
    );
    const firstBudget = Math.max(
      6,
      Math.floor((paper.h - CHROME_MM - HEADER_MM) / MM_PER_LINE),
    );

    const out = [];
    let current = [];
    let used = 0;
    let limit = firstBudget;

    lines.forEach((line) => {
      const w = lineWeight(line);
      /* Never emit an empty page: a row heavier than a whole sheet still has
         to go somewhere, so it starts a page and is allowed to overflow. */
      if (current.length && used + w > limit) {
        out.push(current);
        current = [];
        used = 0;
        limit = budget;
      }
      current.push(line);
      used += w;
    });

    if (current.length) out.push(current);
    return out;
  }, [lines, paper.h, lineWeight]);

  /* The sectioned document: its own widths, its own lines, its own pages.
   *
   * A section contributes a title line and one line per row. Pagination is
   * the same weight-based flow the single-table path uses, so a long section
   * runs across sheets and the next one starts wherever the last finished —
   * three tables that each began on a fresh page would waste most of two. */
  const sectionDoc = useMemo(() => {
    if (!sections?.length) return null;

    const SERIAL_PX = 34;

    const layouts = sections.map((sec) => {
      const widths = (sec.columns || []).map((c) => parseFloat(c.width) || 60);
      const contentPx = widths.reduce((n, w) => n + w, SERIAL_PX);
      return {
        widths,
        serialPx: SERIAL_PX,
        neededMm: contentPx / MM_TO_PX + PAGE_PADDING_MM,
        charsPerLine: widths.map((w) =>
          Math.max(3, Math.floor(w / (dense ? PX_PER_CHAR_DENSE : PX_PER_CHAR))),
        ),
      };
    });

    /* One width for the sheet — the widest section decides it. Sections that
       need less are simply narrower on the page; a page that changed width
       between its own tables would not be a page. */
    const pageWidthMm = Math.max(
      paper.w,
      ...layouts.map((l) => Math.ceil(l.neededMm)),
    );

    /* How many printed lines a row costs, from the longest cell in it. Same
       reasoning as the single-table path: a wrapped cell pushes the rows
       under it down, and a page break estimated on row COUNT puts them off
       the bottom of the sheet. */
    const weigh = (sec, layout, row) => {
      let most = 1;
      (sec.columns || []).forEach((c, i) => {
        const text = String(
          (c.value ? c.value(row) : row[c.key]) ?? "",
        );
        const chars = layout.charsPerLine[i] || 20;
        most = Math.max(most, Math.ceil(text.length / chars));
      });
      return Math.min(most, 6);
    };

    const lines = [];
    sections.forEach((sec, si) => {
      lines.push({ kind: "stitle", si, weight: 2.2 });
      const rows_ = sec.rows || [];
      if (!rows_.length) {
        lines.push({ kind: "sempty", si, weight: 1 });
        return;
      }
      rows_.forEach((row, i) =>
        lines.push({
          kind: "srow",
          si,
          row,
          serial: i + 1,
          weight: weigh(sec, layouts[si], row),
        }),
      );
    });

    const budget = Math.max(8, Math.floor((paper.h - CHROME_MM) / MM_PER_LINE));
    const firstBudget = Math.max(
      6,
      Math.floor((paper.h - CHROME_MM - HEADER_MM) / MM_PER_LINE),
    );

    const out = [];
    let current = [];
    let used = 0;
    let limit = firstBudget;

    lines.forEach((line) => {
      /* A title is never left stranded at the foot of a sheet with its table
         overleaf — it takes the break with it. */
      const orphanTitle =
        line.kind === "stitle" && current.length && used + line.weight + 2 > limit;

      if (current.length && (orphanTitle || used + line.weight > limit)) {
        out.push(current);
        current = [];
        used = 0;
        limit = budget;
      }
      current.push(line);
      used += line.weight;
    });
    if (current.length) out.push(current);

    return { layouts, pageWidthMm, pages: out.length ? out : [[]] };
  }, [sections, paper.w, paper.h, dense]);

  /* A new report starts at the top; without this, opening a short report
     after a long one leaves the viewer parked on a page that no longer
     exists. */
  useEffect(() => {
    if (show) setPage(0);
  }, [show, title, groupBy?.key, rows]);

  const goTo = useCallback(
    (i) => {
      const next = Math.max(0, Math.min(pages.length - 1, i));
      setPage(next);
      pageRefs.current[next]?.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    [pages.length],
  );

  /* Keep the page counter honest while the user scrolls by hand, rather
     than only when they use the arrows. */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !show) return;

    const onScroll = () => {
      const mid = el.scrollTop + el.clientHeight / 3;
      let best = 0;
      pageRefs.current.forEach((node, i) => {
        if (node && node.offsetTop <= mid) best = i;
      });
      setPage(best);
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [show, pages.length]);

  useEffect(() => {
    if (!show) return;
    const onKey = (e) => {
      /* Escape closes a preview laid over the page. Embedded, there is
         nothing to close — the report is the page. */
      if (e.key === "Escape" && !embedded) onClose?.();
      if (e.key === "ArrowRight" || e.key === "PageDown") goTo(page + 1);
      if (e.key === "ArrowLeft" || e.key === "PageUp") goTo(page - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [show, page, goTo, onClose, embedded]);

  /* Restoring on the afterprint event rather than straight after
     window.print() matters: in Safari and Firefox the call returns before the
     dialog closes, so an immediate restore would pull the document out from
     under the print job. The timeout covers browsers that never fire it. */
  useEffect(() => {
    if (!printMode) return;

    let done = false;
    const restore = () => {
      if (done) return;
      done = true;
      setPrintMode(false);
    };

    window.addEventListener("afterprint", restore);
    const raf = requestAnimationFrame(() => window.print());
    const timer = setTimeout(restore, 60000);

    return () => {
      window.removeEventListener("afterprint", restore);
      cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, [printMode]);

  const handlePrint = () => {
    if (embedded) setPrintMode(true);
    else window.print();
  };

  /* Export is done here, from the viewer's own lines and columns, so the file
     is the document: same columns switched on, same grouping, same sub-totals.
     It used to be handed off to the host page, which exported a different set
     of rows and columns entirely — and, because the page's handler ignored the
     format it was given, produced a PDF whichever option was clicked. */
  const runExport = async (format) => {
    /* A sectioned document is not one table, and the single-table exporters
       cannot see its sections — pointed at the Support Team Report they wrote
       out whatever `rows`/`columns` happened to hold, which is the User Query
       detail. The right report was on screen and the wrong one came out of
       the button. */
    if (sectionDoc) {
      setExporting(true);
      try {
        const payload = {
          fileName: `${title}${subtitle ? ` - ${subtitle}` : ""}`.replace(
            /[\\/:*?"<>|]/g,
            "_",
          ),
          company,
          title,
          subtitle,
          dateRange,
          sections,
          paper: { w: sectionDoc.pageWidthMm, h: paper.h },
        };

        if (format === "pdf") exportSectionsPdf(payload);
        else exportSectionsSheet({ ...payload, bookType: format });
      } catch (err) {
        console.error("Report export failed:", err);
        // eslint-disable-next-line no-alert
        window.alert(
          `Could not produce the ${format === "pdf" ? "PDF" : "spreadsheet"}: ${err.message}`,
        );
      } finally {
        setExporting(false);
      }
      return;
    }

    if (!rows.length) {
      // eslint-disable-next-line no-alert
      window.alert("No data available to export.");
      return;
    }

    setExporting(true);
    try {
      const payload = {
        fileName: `${title}${subtitle ? ` - ${subtitle}` : ""}`.replace(
          /[\\/:*?"<>|]/g,
          "_",
        ),
        company,
        title,
        subtitle,
        dateRange,
        columns,
        lines,
        cellValue,
        groupLabel: groupBy?.label ?? "",
        paper: { w: layout.pageWidthMm, h: paper.h },
      };

      if (format === "pdf") exportReportPdf(payload);
      else exportReportSheet({ ...payload, bookType: format });
    } catch (err) {
      console.error("Report export failed:", err);
      // eslint-disable-next-line no-alert
      window.alert(
        `Could not produce the ${format === "pdf" ? "PDF" : "spreadsheet"}: ${err.message}`,
      );
    } finally {
      setExporting(false);
    }
  };

  /* Both toolbar menus close on an outside click. The column list in
     particular is tall enough to sit over the report, so leaving it open
     until its own button is pressed again gets in the way. */
  useEffect(() => {
    if (!showColumns && !showExport) return;

    const onDown = (e) => {
      /* Both bars, not just the dark one.
       *
       * This tested for .rv-toolbar alone, which was true while every control
       * lived in a single strip. The split chrome moved Export into
       * .rv-context above it — so pressing a format fired mousedown, this saw
       * a target outside .rv-toolbar, closed the menu, and unmounted the
       * button before its click could land. The menu opened, the click did
       * nothing, and Export looked broken on exactly the two reports that use
       * the split bars. */
      if (!e.target.closest?.(".rv-toolbar, .rv-context")) {
        setShowColumns(false);
        setShowExport(false);
      }
    };

    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [showColumns, showExport]);

  /* @page cannot read a CSS variable, so the chosen sheet size is written
     into a real stylesheet. Without this the browser keeps printing A4
     portrait and a landscape report comes out cropped down the right side. */
  useEffect(() => {
    if (!show) return;

    const el = document.createElement("style");
    el.setAttribute("data-rv-page", "");
    /* The widened sheet, not the picked one — printing a 468mm-wide report
       onto a 297mm page would just cut the right-hand columns off, which is
       the exact problem the widening exists to avoid. */
    el.textContent = `@media print { @page { size: ${layout.pageWidthMm}mm ${paper.h}mm; margin: 0; } }`;
    document.head.appendChild(el);

    return () => el.remove();
  }, [show, layout.pageWidthMm, paper.h]);

  const toggleFullscreen = () => {
    const el = overlayRef.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen?.();
    else el.requestFullscreen?.();
  };

  const zoomBy = (dir) => {
    const i = ZOOM_STEPS.indexOf(zoom);
    const next = ZOOM_STEPS[Math.max(0, Math.min(ZOOM_STEPS.length - 1, i + dir))];
    setZoom(next ?? zoom);
  };

  if (!show) return null;

  const totalCols = columns.length + 1; // + the S.No column
  const rangeText =
    dateRange?.startDate && dateRange?.endDate
      ? `From : ${fmtDate(dateRange.startDate)}   To : ${fmtDate(dateRange.endDate)}`
      : null;

  const renderTotalsRow = (line, label, className) => (
    <tr className={className}>
      <td />
      {columns.map((c, i) => {
        /* The label sits in the first non-total column so it reads as a
           sentence — "Sub Total ... 147" — instead of floating in column 1. */
        const isLabelCell = i === 0 && !c.total;
        return (
          <td key={c.key} className={c.numeric || c.total ? "rv-num" : ""}>
            {c.total
              ? displayTotal(c, line.totals[c.key])
              : isLabelCell
                ? `${label} (${line.count})`
                : ""}
          </td>
        );
      })}
    </tr>
  );

  /* Defined once and placed by whichever bar is in charge. Two copies of an
     export menu would be two copies of its open/close state. */
  /* One number for both documents, so the pager never offers a page the
     sectioned view does not have. */
  /* The appended summary is a sheet of its own, so the pager has to know
     about it or the last page is unreachable. */
  const tailPages = tailSummary && !showSummary && !sectionDoc ? 1 : 0;
  const pageCount =
    (sectionDoc ? sectionDoc.pages.length : pages.length) + tailPages;

  const exportControl = (
    <div className="position-relative">
      <button
        className="rv-btn rv-btn--solid"
        onClick={() => setShowExport((s) => !s)}
        disabled={exporting}
      >
        <i className={`bi ${exporting ? "bi-hourglass-split" : "bi-download"}`} />
        {exporting ? "Exporting…" : "Export"}
        <i className="bi bi-chevron-down" style={{ fontSize: "0.7rem" }} />
      </button>

      {showExport && (
        <div className="rv-menu rv-menu--export">
          {EXPORT_FORMATS.map((f) => (
            <button
              key={f.key}
              onClick={() => {
                setShowExport(false);
                runExport(f.key);
              }}
            >
              <span className={`rv-fmt rv-fmt--${f.tone}`}>{f.tag}</span>
              <span className="d-flex flex-column text-start">
                <span className="rv-fmt__name">{f.name}</span>
                <span className="rv-fmt__note">{f.note}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const backControl = (
    <button className="rv-btn rv-btn--solid" onClick={onClose}>
      <i className="bi bi-arrow-left" />
      {backLabel}
    </button>
  );

  /* One summary table. Drawn by both the toggle view and the appended sheet
     from the same function — they were two copies of nearly the same JSX,
     which is how one of them ended up with a column the other did not. */
  const renderSummaryTable = (table, dimLabel) => (
    <table
      className={`rv-table rv-table--compact${dense ? " rv-table--dense" : ""}`}
      style={{ marginTop: "6mm" }}
    >
      <thead>
        <tr>
          <th style={{ width: "40px" }}>S.No</th>
          <th className="rv-label">{dimLabel}</th>
          <th className="rv-num" style={{ width: "62px" }}>
            Tasks
          </th>
          {/* The queues a task can be raised under, counted beside the total
              they add up to. */}
          {table.segments.map((seg) => (
            <th key={seg.key} className="rv-num" style={{ width: "84px" }}>
              {seg.label}
            </th>
          ))}
          {table.totalCols.map((c) => (
            <th key={c.key} className="rv-num" style={{ width: "86px" }}>
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {table.rows.map((r, i) => (
          <tr key={r.label}>
            <td>{i + 1}</td>
            <td className="rv-label">{r.label}</td>
            <td className="rv-num">{r.count}</td>
            {table.segments.map((seg) => (
              <td key={seg.key} className="rv-num">
                {r.segments?.[seg.key] || "-"}
              </td>
            ))}
            {table.totalCols.map((c) => (
              <td key={c.key} className="rv-num">
                {displayTotal(c, r.totals?.[c.key] ?? 0)}
              </td>
            ))}
          </tr>
        ))}

        <tr className="rv-grand-row">
          <td />
          <td className="rv-label">GRAND TOTAL ({table.rows.length})</td>
          <td className="rv-num">{table.totals.count}</td>
          {table.segments.map((seg) => (
            <td key={seg.key} className="rv-num">
              {table.totals.segments?.[seg.key] || "-"}
            </td>
          ))}
          {table.totalCols.map((c) => (
            <td key={c.key} className="rv-num">
              {displayTotal(c, table.totals[c.key] ?? 0)}
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  );

  const body = (
    <div
      className={`rv-overlay${usePortal ? "" : " rv-overlay--embedded"}`}
      ref={overlayRef}
      style={!usePortal && fitHeight ? { height: `${fitHeight}px` } : undefined}
    >
      {/* No title in the toolbar.

          The document's own letterhead says the same words a few centimetres
          below — "USER QUERY REPORT — USER WISE" — so the bar was spending a
          whole row repeating it, and with the controls unable to fit beside
          it the strip wrapped to two rows. Dropping it fits everything on one
          and gives the page back the height. */}
      {splitChrome && (
        <div className="rv-context">
          <div className="rv-context__side">{contextLeft}</div>

          {/* The title the one-bar chrome deliberately leaves out. It earns
              its place here: with the setup screen behind you, this bar is
              the only thing on screen that says which report you asked for
              before the sheet scrolls its own letterhead into view. */}
          <div className="rv-context__title">
            <i className="bi bi-file-earmark-text" />
            <span>
              {title}
              {subtitle ? ` — ${subtitle}` : ""}
            </span>
          </div>

          <div className="rv-context__side rv-context__side--end">
            {exportControl}
            {onClose && backControl}
          </div>
        </div>
      )}

      <div className={`rv-toolbar${splitChrome ? " rv-toolbar--dark" : ""}`}>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <div className="rv-group">
            <button className="rv-btn" onClick={() => goTo(0)} disabled={page === 0} title="First page">
              <i className="bi bi-chevron-double-left" />
            </button>
            <button className="rv-btn" onClick={() => goTo(page - 1)} disabled={page === 0} title="Previous page">
              <i className="bi bi-chevron-left" />
            </button>
            <span className="rv-pageno">
              {page + 1} / {pageCount}
            </span>
            <button
              className="rv-btn"
              onClick={() => goTo(page + 1)}
              disabled={page >= pageCount - 1}
              title="Next page"
            >
              <i className="bi bi-chevron-right" />
            </button>
            <button
              className="rv-btn"
              onClick={() => goTo(pageCount - 1)}
              disabled={page >= pageCount - 1}
              title="Last page"
            >
              <i className="bi bi-chevron-double-right" />
            </button>
          </div>

          <div className="rv-group">
            <button className="rv-btn" onClick={() => zoomBy(-1)} disabled={zoom === ZOOM_STEPS[0]} title="Zoom out">
              <i className="bi bi-zoom-out" />
            </button>
            <span className="rv-pageno">{zoom}%</span>
            <button
              className="rv-btn"
              onClick={() => zoomBy(1)}
              disabled={zoom === ZOOM_STEPS[ZOOM_STEPS.length - 1]}
              title="Zoom in"
            >
              <i className="bi bi-zoom-in" />
            </button>
          </div>

          {/* Columns, Detail/Summary and the paper size all address ONE table.
              A sectioned document has three, each with its own columns, so
              the first two are hidden rather than left on screen doing
              nothing to the report in front of you. */}
          {!sectionDoc && (
          <div className="position-relative">
            <button
              className="rv-btn"
              onClick={() => setShowColumns((s) => !s)}
              title="Choose columns"
            >
              <i className="bi bi-layout-three-columns" />
              {columns.length}/{allColumns.length}
              <i className="bi bi-chevron-down" style={{ fontSize: "0.7rem" }} />
            </button>

            {showColumns && (
              <div className="rv-menu rv-menu--cols">
                <div className="rv-menu__head">
                  <button type="button" onClick={() => setHidden([])}>
                    Show all
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      /* Never hide the last one — a table with no columns is
                         a stack of blank sheets. */
                      setHidden(allColumns.slice(1).map((c) => c.key))
                    }
                  >
                    Clear
                  </button>
                </div>

                {allColumns.map((c) => {
                  const on = !hidden.includes(c.key);
                  return (
                    <label key={c.key} className="rv-menu__check">
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() =>
                          setHidden((h) =>
                            on
                              ? h.length === allColumns.length - 1
                                ? h
                                : [...h, c.key]
                              : h.filter((k) => k !== c.key),
                          )
                        }
                      />
                      <span>{c.label}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          )}

          {/* Detail / Summary. Disabled without a grouping, because a summary
              of one group is just the grand total said twice. */}
          {!sectionDoc && (
          <div className="rv-group">
            {[
              { key: false, label: "Detail", icon: "bi-list-ul" },
              { key: true, label: "Summary", icon: "bi-bar-chart-line" },
            ].map((v) => (
              <button
                key={String(v.key)}
                className={`rv-btn${summary === v.key ? " rv-btn--on" : ""}`}
                onClick={() => setSummary(v.key)}
                disabled={v.key && !canSummarise}
                title={
                  v.key && !canSummarise
                    ? "Pick a report type first — a summary needs something to group by"
                    : v.label
                }
              >
                <i className={`bi ${v.icon}`} />
                {v.label}
              </button>
            ))}
          </div>

          )}

          <div className="rv-group">
            {/* Paper size. Landscape is the useful default for a wide report;
                A3 is there for the ones that still will not fit. */}
            <select
              className="rv-select"
              value={paperKey}
              onChange={(e) => setPaperKey(e.target.value)}
              title="Paper size"
            >
              {Object.entries(PAPERS).map(([k, p]) => (
                <option key={k} value={k}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>

          <div className="rv-group">
            <button className="rv-btn" onClick={handlePrint} title="Print">
              <i className="bi bi-printer" />
            </button>
            <button className="rv-btn" onClick={toggleFullscreen} title="Fullscreen">
              <i className="bi bi-arrows-fullscreen" />
            </button>
          </div>

          {!splitChrome && exportControl}

          {/* Nothing to go back to when the report is the page itself — unless
              the page put it there, which is what splitChrome means. */}
          {!splitChrome && !embedded && backControl}
        </div>
      </div>

      <div className="rv-scroll" ref={scrollRef}>
        {sectionDoc ? (
          sectionDoc.pages.map((pageLines, pi) => {
            /* Consecutive lines of one section become one table. A section
               split across a page break therefore gets its column headings
               repeated at the top of the next sheet, which is the whole
               reason the headings are drawn per run rather than once. */
            const runs = [];
            pageLines.forEach((line) => {
              const last = runs[runs.length - 1];
              if (last && last.si === line.si) last.lines.push(line);
              else runs.push({ si: line.si, lines: [line] });
            });

            return (
              <div
                key={pi}
                ref={(el) => (pageRefs.current[pi] = el)}
                className="rv-page-wrap"
                style={{
                  transform: `scale(${zoom / 100})`,
                  height:
                    zoom !== 100
                      ? `calc(${paper.h}mm * ${zoom / 100})`
                      : undefined,
                }}
              >
                <div
                  className="rv-page"
                  style={{
                    width: `${sectionDoc.pageWidthMm}mm`,
                    minHeight: `${paper.h}mm`,
                  }}
                >
                  <div className="rv-printed">Report Printed : {printedAt}</div>

                  <div className="rv-head">
                    <img src={skyRPLogo} alt="" className="rv-head__logo" />
                    <div className="rv-head__text">
                      <h1 className="rv-head__company">{company}</h1>
                      <h2 className="rv-head__title">
                        {title}
                        {subtitle ? ` — ${subtitle}` : ""}
                      </h2>
                      {rangeText && (
                        <div className="rv-head__range">{rangeText}</div>
                      )}
                    </div>
                    <div style={{ width: "62px", flex: "0 0 auto" }} />
                  </div>

                  <div className="rv-rule" />

                  {runs.map((run, ri) => {
                    const sec = sections[run.si];
                    const layout = sectionDoc.layouts[run.si];
                    const cols = sec.columns || [];
                    const startsHere = run.lines[0].kind === "stitle";
                    const bodyLines = run.lines.filter(
                      (l) => l.kind !== "stitle",
                    );

                    return (
                      <div key={`${pi}-${ri}`} className="rv-section">
                        {/* The banner only where the section begins. On a
                            continuation sheet the repeated column headings
                            say which table this is, and a second "(cont.)"
                            title would push a row off every page. */}
                        {startsHere && (
                          <div className="rv-section__title">{sec.title}</div>
                        )}

                        <table
                          className={`rv-table${dense ? " rv-table--dense" : ""}`}
                        >
                          <thead>
                            <tr>
                              <th style={{ width: `${layout.serialPx}px` }}>
                                S.No
                              </th>
                              {cols.map((c, i) => (
                                <th
                                  key={c.key}
                                  style={{ width: `${layout.widths[i]}px` }}
                                  className={c.numeric ? "rv-num" : ""}
                                >
                                  {c.label}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {bodyLines.map((line, li) =>
                              line.kind === "sempty" ? (
                                <tr key={li}>
                                  <td
                                    colSpan={cols.length + 1}
                                    className="rv-empty"
                                  >
                                    Nothing to report in this section.
                                  </td>
                                </tr>
                              ) : (
                                <tr
                                  key={li}
                                  className={
                                    sec.isTotalRow && sec.isTotalRow(line.row)
                                      ? "rv-grand-row"
                                      : undefined
                                  }
                                >
                                  {/* A totals line is not the 27th client,
                                      so it carries no serial. */}
                                  <td>
                                    {sec.isTotalRow && sec.isTotalRow(line.row)
                                      ? ""
                                      : line.serial}
                                  </td>
                                  {cols.map((c) => {
                                    const v = c.value
                                      ? c.value(line.row)
                                      : line.row[c.key];
                                    return (
                                      <td
                                        key={c.key}
                                        className={c.numeric ? "rv-num" : ""}
                                        style={
                                          c.cellStyle
                                            ? c.cellStyle(v, line.row)
                                            : undefined
                                        }
                                      >
                                        {v === null ||
                                        v === undefined ||
                                        v === ""
                                          ? "-"
                                          : v}
                                      </td>
                                    );
                                  })}
                                </tr>
                              ),
                            )}
                          </tbody>
                        </table>
                      </div>
                    );
                  })}

                  <div className="rv-foot">
                    <span>{company}</span>
                    <span>
                      Page {pi + 1} of {sectionDoc.pages.length}
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        ) : showSummary ? (
          <div className="rv-page-wrap" style={{ transform: `scale(${zoom / 100})` }}>
            <div
              className="rv-page"
              style={{
                width: `${paper.w}mm`,
                minHeight: `${paper.h}mm`,
              }}
            >
              <div className="rv-printed">Report Printed : {printedAt}</div>

              <div className="rv-head">
                <img src={skyRPLogo} alt="" className="rv-head__logo" />
                <div className="rv-head__text">
                  <h1 className="rv-head__company">{company}</h1>
                  <h2 className="rv-head__title">
                    {title} SUMMARY{subtitle ? ` — ${subtitle}` : ""}
                  </h2>
                  {rangeText && <div className="rv-head__range">{rangeText}</div>}
                </div>
                <div style={{ width: "62px", flex: "0 0 auto" }} />
              </div>

              <div className="rv-rule" />

              <ReportSummaryChart
                rows={summaryRows}
                metricKey="count"
                metricLabel="Tasks"
              />

              {summaryTable &&
                renderSummaryTable(
                  summaryTable,
                  groupBy?.label ?? summaryBy?.label ?? "",
                )}

              <div className="rv-foot">
                <span>{company}</span>
                <span>Summary</span>
              </div>
            </div>
          </div>
        ) : (
        pages.map((pageLines, pi) => (
          <div
            key={pi}
            ref={(el) => (pageRefs.current[pi] = el)}
            className="rv-page-wrap"
            style={{
              transform: `scale(${zoom / 100})`,
              /* Reserve the scaled height. transform does not affect layout,
                 so without this a zoomed-in document overlaps the next sheet
                 and the last page cannot be scrolled to. */
              height:
                zoom !== 100
                  ? `calc(${paper.h}mm * ${zoom / 100})`
                  : undefined,
            }}
          >
            <div
              className="rv-page"
              style={{
                width: `${layout.pageWidthMm}mm`,
                minHeight: `${paper.h}mm`,
              }}
            >
              <div className="rv-printed">Report Printed : {printedAt}</div>

              <div className="rv-head">
                <img src={skyRPLogo} alt="" className="rv-head__logo" />
                <div className="rv-head__text">
                  <h1 className="rv-head__company">{company}</h1>
                  <h2 className="rv-head__title">
                    {title}
                    {subtitle ? ` — ${subtitle}` : ""}
                  </h2>
                  {rangeText && <div className="rv-head__range">{rangeText}</div>}
                </div>
                {/* Balances the logo so the block above stays optically centred. */}
                <div style={{ width: "62px", flex: "0 0 auto" }} />
              </div>

              <div className="rv-rule" />

              <table className={`rv-table${dense ? " rv-table--dense" : ""}`}>
                <thead>
                  <tr>
                    <th style={{ width: `${layout.serialPx}px` }}>S.No</th>
                    {columns.map((c, i) => (
                      <th
                        key={c.key}
                        style={{ width: `${layout.widths[i]}px` }}
                        className={c.numeric || c.total ? "rv-num" : ""}
                      >
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageLines.length === 0 && (
                    <tr>
                      <td colSpan={totalCols} className="rv-empty">
                        No records for the selected filters.
                      </td>
                    </tr>
                  )}

                  {pageLines.map((line, li) => {
                    if (line.kind === "group") {
                      return (
                        <tr key={li} className="rv-group-row">
                          <td colSpan={totalCols}>
                            {groupBy.label} : {line.label}
                          </td>
                        </tr>
                      );
                    }

                    if (line.kind === "subtotal") {
                      return (
                        <React.Fragment key={li}>
                          {renderTotalsRow(line, "Sub Total", "rv-subtotal-row")}
                        </React.Fragment>
                      );
                    }

                    if (line.kind === "grand") {
                      return (
                        <React.Fragment key={li}>
                          {renderTotalsRow(line, "GRAND TOTAL", "rv-grand-row")}
                        </React.Fragment>
                      );
                    }

                    return (
                      <tr key={li}>
                        <td>{line.serial}</td>
                        {columns.map((c) => {
                          const v = cellValue(c, line.row);
                          const blank =
                            v === null || v === undefined || v === "";
                          return (
                            <td
                              key={c.key}
                              className={c.numeric || c.total ? "rv-num" : ""}
                              /* Nothing to colour in an empty cell — a tint
                                 behind a lone dash reads as a value. */
                              style={blank ? undefined : c.cellStyle?.(v, line.row)}
                            >
                              {blank ? "-" : String(displayCell(c, v))}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div className="rv-foot">
                <span>{company}</span>
                <span>
                  Page {pi + 1} of {pages.length}
                </span>
              </div>
            </div>
          </div>
        )))}

        {/* The summary, printed after the detail rather than instead of it.
            Its own sheet: appending it to the last page would need a page
            break estimated for a table that has not been laid out yet, and a
            summary split across the fold is the one table that must not be. */}
        {tailSummary && !showSummary && !sectionDoc && (
          <div
            key="tail-summary"
            ref={(el) => (pageRefs.current[pages.length] = el)}
            className="rv-page-wrap"
            style={{
              transform: `scale(${zoom / 100})`,
              height:
                zoom !== 100 ? `calc(${paper.h}mm * ${zoom / 100})` : undefined,
            }}
          >
            <div
              className="rv-page"
              style={{
                width: `${layout.pageWidthMm}mm`,
                minHeight: `${paper.h}mm`,
              }}
            >
              <div className="rv-printed">Report Printed : {printedAt}</div>

              <div className="rv-head">
                <img src={skyRPLogo} alt="" className="rv-head__logo" />
                <div className="rv-head__text">
                  <h1 className="rv-head__company">{company}</h1>
                  <h2 className="rv-head__title">
                    {title}
                    {subtitle ? ` \u2014 ${subtitle}` : ""}
                  </h2>
                  {rangeText && <div className="rv-head__range">{rangeText}</div>}
                </div>
                <div style={{ width: "62px", flex: "0 0 auto" }} />
              </div>

              <div className="rv-rule" />

              <div className="rv-section">
                <div className="rv-section__title">
                  Summary by {tailSummary.label}
                </div>

                {renderSummaryTable(tailSummary.table, tailSummary.label)}
              </div>

              <div className="rv-foot">
                <span>{company}</span>
                <span>Summary</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return usePortal ? ReactDOM.createPortal(body, document.body) : body;
};

export default ReportViewer;
